import {
  ATTACHMENT_UPLOAD_RULES,
  mimeTypeForUpload,
  uploadKindOfMime,
  type AttachmentKind,
  type UploadKind,
} from '@chatapp-inbox/shared';

/** Quantos arquivos se anexam de uma vez (cada um vira uma mensagem). */
export const MAX_FILES_AT_ONCE = 10;

/** O que o WhatsApp aceita, em palavras: aparece quando um arquivo é recusado. */
export const ACCEPTED_TYPES_HELP =
  'Aceitos: imagens (JPG, PNG), vídeo (MP4), áudio (MP3, AAC, OGG, AMR) e documentos (PDF, Word, Excel, PowerPoint, TXT).';

/** Nome curto de cada família, para a prévia da conversa e para leitores de tela. */
export const KIND_LABEL: Record<AttachmentKind, string> = {
  image: 'Foto',
  video: 'Vídeo',
  audio: 'Áudio',
  document: 'Documento',
  sticker: 'Figurinha',
};

/** "12 B", "340 KB", "1,2 MB": uma casa decimal só a partir de MB, com vírgula. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1).replace('.', ',') : Math.round(mb)} MB`;
}

export type FileCheck =
  { ok: true; kind: UploadKind; mimeType: string } | { ok: false; message: string };

/**
 * Confere um arquivo ANTES de enviar (o servidor confere de novo, inclusive o conteúdo): tipo que o
 * WhatsApp aceita e tamanho dentro do limite da família. A mensagem diz o que fazer.
 */
export function checkFile(file: Pick<File, 'name' | 'type' | 'size'>): FileCheck {
  const name = file.name || 'arquivo';
  const mimeType = mimeTypeForUpload(file.type, file.name);
  const kind = mimeType ? uploadKindOfMime(mimeType) : null;
  if (!mimeType || !kind) {
    return {
      ok: false,
      message: `"${name}": este tipo de arquivo não pode ser enviado pelo WhatsApp. ${ACCEPTED_TYPES_HELP}`,
    };
  }
  if (file.size === 0) return { ok: false, message: `"${name}": o arquivo está vazio.` };
  const rule = ATTACHMENT_UPLOAD_RULES[kind];
  if (file.size > rule.maxBytes) {
    return {
      ok: false,
      message: `"${name}": ${rule.label.toLowerCase()} pode ter até ${formatBytes(rule.maxBytes)} (este tem ${formatBytes(file.size)}).`,
    };
  }
  return { ok: true, kind, mimeType };
}

/** Um arquivo escolhido no compositor, ainda não enviado. */
export interface PendingFile {
  id: string;
  file: File;
  kind: UploadKind;
  mimeType: string;
  /** Miniatura (imagem): `URL.createObjectURL`, revogada quando o arquivo sai da lista. */
  previewUrl: string | null;
}

/** O arquivo ganha uma miniatura local só se for imagem e o navegador souber fazê-la. */
export function previewUrlFor(file: File, kind: UploadKind): string | null {
  if (kind !== 'image' || typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(file);
}

export function revokePreview(url: string | null): void {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

/**
 * Ordem de envio de vários arquivos com um texto: o texto vira a legenda do primeiro arquivo que
 * aceita legenda (áudio não aceita); se não há nenhum, ele sai antes, como mensagem de texto.
 * Devolve o índice do arquivo que leva a legenda, ou -1.
 */
export function captionTarget(files: readonly Pick<PendingFile, 'kind'>[]): number {
  return files.findIndex((f) => f.kind !== 'audio');
}
